// The scan response as the marketing site publishes it: the cards on the
// landing page and the table at /document-field-explorer.
//
// The rows are NOT written here. They are generated from the contract's own
// schema into ./scan-shape.generated.ts, and `pnpm check` fails when the
// committed copy and the schema disagree (scripts/gen-scan-fields.mjs). This
// page was hand-written once and drifted into advertising keys the API has
// never returned, which a visitor read as the contract.
//
// What is written here is the editorial copy each group is shown under — a
// heading and one sentence aimed at somebody deciding whether this API answers
// their question. A schema cannot supply that, and a field description written
// for an API reference does not read as a blurb. Everything else comes from
// the schema.

import { type ScanShapeNode, scanShape } from "./scan-shape.generated";

export type FieldNode = ScanShapeNode;

export interface FieldGroup {
  /** The response key this group is, which is also its anchor on the page. */
  readonly key: string;
  readonly title: string;
  readonly summary: string;
  readonly fields: readonly FieldNode[];
}

// One entry per top-level key of the response. A key added to the contract
// without a line here shows up as a group with no blurb, which the suite beside
// this file fails on rather than leaving on the page.
const GROUP_SUMMARIES: Readonly<Record<string, string>> = {
  meta: "What this scan is and how it went: its id, its status, whether it drew a credit, and where the time went.",
  document:
    "What kind of document was recognised, which state issued it, its number and dates, and whether it has expired. Null when nothing was recognised.",
  holder: "The person the document identifies. Null when nothing was recognised.",
  fields:
    "Every field read off the printed page, re-keyed to a stable name. One entry per reading, each with its own language and confidence band.",
  mrz: "The machine-readable zone: a verdict on whether it checks out, the reason when it does not, and the lines themselves exactly as read.",
  images: "Crops taken from the picture you sent, each one a data URL.",
  quality: "Whether the picture you sent was good enough to recognise from.",
  authenticity:
    "Authenticity verification. It does not run under the recognition-only call, which reports it as not checked.",
};

export function toGroups(
  shape: readonly ScanShapeNode[],
  summaries: Readonly<Record<string, string>> = GROUP_SUMMARIES,
): readonly FieldGroup[] {
  return shape.map((group) => {
    const summary = summaries[group.name];
    // Loud rather than blank. A group added to the contract reaches this page
    // automatically, and the one thing the schema cannot supply is the sentence
    // under the heading — so a missing one stops the build instead of shipping
    // a card that says nothing.
    if (summary === undefined) {
      throw new Error(`The response group \`${group.name}\` has no blurb in $lib/seo/fields.`);
    }
    return {
      key: group.name,
      // An array of entries is titled as one, so a card says what shape it is
      // before the reader opens anything.
      title: group.type.endsWith("[]") ? `${group.name}[]` : group.name,
      summary,
      fields: group.children ?? [],
    };
  });
}

export const scanFieldGroups: readonly FieldGroup[] = toGroups(scanShape);
